'use client';
import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  Dialog,
  IconButton,
  DialogActions as MuiDialogActions,
  DialogContent as MuiDialogContent,
  DialogTitle as MuiDialogTitle,
  Typography,
} from '@mui/material';
import { sendGTMEvent } from '@next/third-parties/google';
import type { ReactNode } from 'react';
import { useCourses } from '@/providers';
import { useViewStore } from '@/stores';
import { getFirstNavigableChapter } from '@/utils/client/course-curriculum';
import { useRoute } from '@/utils/client/route';

function DialogTitle({
  id,
  children,
  onClose,
  lock,
}: {
  id: string;
  children: ReactNode;
  onClose?: () => void;
  lock?: boolean;
}) {
  return (
    <MuiDialogTitle component="div" id={id} sx={{ m: 0, p: 2, position: 'relative' }}>
      <Typography variant="h6">{children}</Typography>
      {onClose && lock !== true && (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
          size="large">
          <CloseIcon />
        </IconButton>
      )}
    </MuiDialogTitle>
  );
}

export default function MymDialog() {
  const { goToRoute } = useRoute();
  const { renewTest, selectedCourse, selectedChapter, updateUserExercise, submitTest } =
    useCourses();
  const { dialog, toggleDialog } = useViewStore();

  const handleClose = () => {
    if (!dialog?.lock) {
      toggleDialog();
    }
  };

  const handleAction = async () => {
    switch (dialog?.action) {
      case 'submitTest':
        submitTest();
        sendGTMEvent({
          event: 'submit_test',
          category: 'Test',
          action: 'Submit',
          label: selectedCourse?.slug,
        });
        handleClose();
        break;
      case 'submitTestExercise':
        sendGTMEvent({
          event: 'submit_user_exercise',
          category: 'Exercise',
          action: 'Submit',
          label: selectedChapter?.slug,
        });
        updateUserExercise(true);
        handleClose();
        break;
      case 'renewTest':
        sendGTMEvent({
          event: 'renew_test',
          category: 'Test',
          action: 'Renew',
          label: selectedCourse?.slug,
        });
        renewTest();
        goToRoute('myCourseLearn', {
          courseSlug: selectedCourse?.slug ?? '',
          chapterSlug: getFirstNavigableChapter(selectedCourse?.curriculum ?? [])?.item.slug ?? '',
        });
        handleClose();
        break;
      default:
        console.error('Unknown action');
    }
  };

  return (
    <Dialog onClose={handleClose} aria-labelledby="customized-dialog-title" open={!!dialog}>
      <DialogTitle id="customized-dialog-title" onClose={handleClose} lock={dialog?.lock}>
        {dialog?.title}
      </DialogTitle>
      <MuiDialogContent dividers sx={{ p: 2 }}>
        {dialog?.content}
      </MuiDialogContent>
      {!dialog?.lock && (
        <MuiDialogActions sx={{ m: 0, p: 1 }}>
          <Button size="small" onClick={handleClose} variant="outlined">
            Abbrechen
          </Button>
          <Button size="small" onClick={handleAction} variant="contained" color="primary">
            {dialog?.actionLabel}
          </Button>
        </MuiDialogActions>
      )}
    </Dialog>
  );
}
